import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Clock } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "@/lib/api";
import { addDaysIso, todayIso } from "@/lib/date-range";
import { formatDateIn, formatTimeIn } from "@/lib/datetime";
import { employeeApi, type EmployeeConfig, type EmployeeDay } from "@/lib/employee-api";

const WINDOW_DAYS = 30;

function duration(mins: number | null): string {
  if (mins == null || mins <= 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

export default function MyAttendance() {
  const [config, setConfig] = useState<EmployeeConfig | null>(null);
  const [days, setDays] = useState<EmployeeDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tz = config?.tenant.timezone ?? "Asia/Kuala_Lumpur";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await employeeApi.config();
      setConfig(cfg);
      // Anchored on the *tenant's* today, not the browser's — an employee
      // travelling or on a device set to another zone should still see their
      // organisation's working days.
      const to = todayIso(cfg.tenant.timezone);
      const res = await employeeApi.history({
        dateFrom: addDaysIso(to, -(WINDOW_DAYS - 1)),
        dateTo: to,
        pageSize: WINDOW_DAYS,
      });
      setDays(res.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load your attendance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Skeleton className="h-64 w-full" />;

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Couldn't load your attendance</AlertTitle>
        <AlertDescription className="flex items-center gap-3">
          {error}
          <Button size="sm" variant="outline" onClick={() => void load()}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Last {WINDOW_DAYS} days</CardTitle>
      </CardHeader>
      <CardContent className="divide-y">
        {days.length === 0 && (
          <p className="py-4 text-sm text-muted-foreground">
            No attendance recorded in this period.
          </p>
        )}
        {days.map((day) => (
          <div key={day.date} className="py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{formatDateIn(day.date, tz)}</span>
              <div className="flex items-center gap-2">
                {day.workMode === "WFH" && <Badge variant="secondary">WFH</Badge>}
                {day.workMode === "WFA" && <Badge variant="secondary">WFA</Badge>}
                {day.isException && <Badge variant="outline">Exception</Badge>}
                <span className="text-sm text-muted-foreground">
                  {duration(day.totalDurationMinutes)}
                </span>
              </div>
            </div>
            <div className="mt-1 space-y-0.5">
              {day.sessions.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                >
                  <Clock className="h-3 w-3" />
                  {formatTimeIn(s.startAt, tz)} →{" "}
                  {s.endAt ? formatTimeIn(s.endAt, tz) : "in progress"}
                  {s.startSource === "WEB" && <span>· web</span>}
                  {s.startGpsStatus === "INVALID" && (
                    <span className="text-warning">· low accuracy</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
